// §4.8 PICK SCREEN (floor PWA) — spec 09 §3.7, §4.8, §9.10, P3-D PR-D3, D-286.
//
// "Per §3.7 — line cards with reserved unit + location ('Bobbin 1645 · WH-C/A3
// · 1600 m'); scan button full-width; after scan: full-unit confirm or numeric
// keypad for cut length with live remainder preview ('picks 400 m → 1200 m
// remains'); mis-scan shows unit's true reservation ('Reserved for
// SO-2026-00087'); Pack / Ship buttons with signature-free confirm; offline
// banner when queued ('3 picks pending sync')."
//
// THE THREE TAPS (§3.7's budget, measured from the queue tap to the picked
// row): the queue's row (1) lands here; a scan (2) — the wedge, the camera, a
// typed serial, or a tap on the planned unit's own row, which is the same act
// when the plan already knows the serial — resolves the unit and opens the
// quantity panel pre-filled with the most likely pick; Confirm (3) enqueues
// ONE `shipment_pick` with the tap-time key. Nothing else stands between the
// row and the ledger.
//
// THE OPEN IS ONLINE AND HAPPENS ON ENTRY. The pick plan — which lines are
// open, which units hold them, where they are, how much is on them — exists
// only on an open shipment (`GET /v1/shipments/{id}`, the screen's one read);
// the order detail names reservation ids, not bobbins. So when no `picking` /
// `packed` shipment exists for the order and the device has signal, the screen
// enqueues `shipment_open` itself rather than spending one of the three taps
// on a "Start picking" press. A second open answers 409 SHIPMENT_OPEN (one
// open per order is DDL — ruling 3), so an entry that races another tablet is
// harmless. In a dead zone with no cached shipment the screen says it needs
// signal ONCE; the §5.4 offline leg then queues picks against a shipment that
// already exists (the plan's own statement — offline open-then-pick is not
// chained as a follow-up). A refused open (ORDER_NOT_SHIPPABLE, CREDIT_BLOCKED,
// a 403 off the facility fence) is rendered with the server's sentence and an
// explicit "Start picking" retry, never re-fired in a loop.
//
// THE REMAINDER PREVIEW IS DISPLAY ONLY (§4.8's own words: "the server's 422
// is the truth"). `remaining_measure` is module 06's 6-dp measure in the UNIT's
// UoM; `qty_picked` is the LINE's 3-dp quantity in the LINE's UoM. When the two
// UoMs agree the preview is exact scaled-integer arithmetic (D-042 — never a
// float); when they differ (risk 3 of the build plan — a kg-base product
// ordered in metres) the client cannot convert honestly and prints no
// remainder at all, and the server's QTY_EXCEEDS_REMAINING is the only
// ceiling. Two refusals ARE provable on the client in the shared frame — more
// than the line has open, more than the unit has left in the same UoM — and a
// body that provably cannot land never enters the outbox (the receive
// screen's rule): Confirm is disabled WITH the sentence inside the button
// (D-118).
//
// THE OVERRIDE (§7's "mis-scan reservation override", ruling 7) is offered
// ONLY to a facility_admin / group_admin lineage and ONLY on `reason:
// not_reserved` — a unit held for ANOTHER order is refused whatever the flag,
// and the card names the holder ("Reserved for SO-2026-00087") so the remedy
// (that order's line cancel, in the office) is the sentence the storekeeper
// carries to the manager. A storekeeper sees the sentence and no button: a
// control whose press answers 403 is D-088's forbidden shape.
//
// "THIS SHIPMENT COMPLETES THE ORDER" is stated BEFORE the press that makes it
// so (A's pre-press disclosure, the D-088 posture PR-D2 used for the last-line
// cancel): the moment the live picks — server rows plus this device's queued
// ones — close every open line, the hint sits above Pack and Ship.
//
// WHAT IS QUEUED AND WHAT IS NOT. Picks, undo, pack and ship ride the ONE
// `assessly.floor` outbox (D-048/D-054) with uuid v7 keys minted at tap; a
// pick refused at sync (§9.10's over-pick after someone else shipped from the
// same bobbin) parks in the needs-attention tray with the 422's OWN sentence
// and the fresh remaining (lib/floor-errors.ts), never silently dropped.
// The scan RESOLUTION of an unplanned unit (the override path) goes through
// the existing `scan` op online and is refused offline with a sentence — a
// planned unit resolves LOCALLY against the pick plan, no round trip, no
// signal, which is the path the three taps take.
//
// NO MONEY on this screen, by construction: `ShipmentDetailResponse` carries
// quantities only (§7's redaction row — the storekeeper holds no
// `sales.read_value`), so there is nothing here to redact.
//
// THE DELIVERY NOTE (P3-D PR-D4, D-288 rulings 10, 11, 14) is drawn once, at
// pack, from the frozen lines, and the paper that travels with the goods is
// that one. Once the shipment reads `packed` (and after it ships, so the
// storekeeper who pressed Ship can still print it for the driver) the screen
// links it: `GET /v1/shipments/{id}/delivery-note.pdf` under the session
// cookie, whose door is `sales.read`, which the storekeeper holds. A holder of
// `sales.read_value` is redirected to the stored note, and the storekeeper,
// who holds no value grant, is answered a copy without prices drawn on demand
// (D-337 ruling 4). Opening it is a NETWORK act on a screen whose contract is offline, so
// with no signal (or an outbox that cannot reach us) the link gives way to a
// sentence rather than a tab that fails. A pack still queued on this device
// has no note yet, and the screen says so. A packed-or-later shipment with no
// note on file — a draw that failed at pack (the pack stood: a glyph the fonts
// lack, or the object store down), or a shipment packed before notes existed —
// gets one neutral sentence ("No delivery note on file.") and a Draw press,
// which calls `POST /v1/shipments/{id}/
// delivery-note` DIRECTLY rather than through the outbox: drawing a document
// changes no ledger row, answers the same shipment whatever it is pressed, and
// is only useful while the operator stands there, so queuing it into a dead
// zone would be a promise nobody reads.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import {
  FloorButton,
  FloorError,
  FloorNeedsConnection,
  FloorScreen,
  FloorSkeleton,
  SnapshotNote,
  syncTone,
} from "../components/floor-chrome";
import { QtyStepper } from "../components/qty-stepper";
import { ScanInput } from "../components/scan-input";
import { floorReadSignal, useCachedRead } from "../hooks/use-cached-read";
import { useFloorOutbox, useFloorSubmit } from "../hooks/use-floor-outbox";
import { useOnline } from "../hooks/use-online";
import { hasLineage } from "../../../lib/role-codes";
import { deviceId } from "../lib/floor-device";
import { floorFailureMessage, floorForbidden, floorRequestId } from "../lib/floor-errors";
import { floorFeedback } from "../lib/floor-feedback";
import type {
  FloorOp,
  PickBody,
  ScanResponse,
  ShipBody,
  ShipmentDetailResponse,
  ShipmentListResponse,
} from "../lib/floor-ops";
import {
  describeFailure,
  type OutboxFailure,
  type OutboxItem,
  type OutboxSettlement,
} from "../lib/floor-outbox";
import { loadRead, readCacheKey, saveRead } from "../lib/floor-read-cache";
import { normalizeQty } from "../lib/qty-entry";
import { useInventoryPermissions } from "../../office/inventory/hooks/permissions";
import { formatMeasure, parseMeasure } from "../../office/inventory/lib/measure";
import { NOTE_STATUSES, deliveryNotePath } from "../../office/sales/lib/documents";
import { client } from "../../../lib/api";
import { newIdempotencyKey, unwrap } from "../../../lib/request";
import { refreshQuery } from "../../../lib/query";
import { unitSymbol } from "../../../lib/quantity";
import { shortId } from "../../../lib/short-id";
import { SHIP_QUEUE_CACHE_KEY } from "./floor-ship-queue";
import "./floor-production.css";
import "./floor-ship.css";

type S = components["schemas"];
type PickPlanLine = S["PickPlanLine"];
type PickPlanUnit = S["PickPlanUnit"];
type ShipmentLine = S["ShipmentLine"];
type ShipmentSummary = S["ShipmentSummary"];
type OrderListResponse = S["OrderListResponse"];

/** A shipment the operator can still act on (one per order — ruling 3). */
const OPEN_STATUSES: ReadonlySet<ShipmentSummary["status"]> = new Set(["picking", "packed"]);

/** A line that can still take a pick (§2.17's mirror — `open` / `ready`). */
const PICKABLE_LINE_STATUSES: ReadonlySet<PickPlanLine["line_status"]> = new Set(["open", "ready"]);

/**
 * The contract's `SalesQty` — `numeric(14,3)`, > 0 — mirrored so a body the
 * server would 400 never enters the queue. Three fraction digits is the WIRE's
 * ceiling for a pick quantity (`qty_picked` is the ORDER LINE's frame, not the
 * unit's 6-dp measure), which is also why the keypad's precision is 3 here:
 * the plan carries no per-UoM `decimal_precision` on a pick-plan line, and the
 * wire's own limit is the honest ceiling the screen can offer.
 */
const SALES_QTY_RE = /^(?=.*[1-9])\d{1,11}(\.\d{1,3})?$/u;
const QTY_PRECISION = 3;

/** The 6-dp scale `parseMeasure` uses (D-042). */
const SCALE = 1_000_000n;

/** A unit the scan resolved, planned (the pick plan named it) or not. */
export interface ResolvedUnit {
  readonly stockUnitId: string;
  readonly serialCode: string;
  readonly locationCode: string | undefined;
  readonly remainingMeasure: string | null;
  readonly measureUom: string | null;
  readonly planned: boolean;
}

/** A pick this device holds that the server has not seen yet. */
export interface QueuedPick {
  readonly lineId: string;
  readonly unitId: string;
  readonly qty: string;
}

type Stage =
  | { readonly kind: "scan" }
  /** An UNPLANNED unit resolved on the wire and more than one line open: the
   * operator names the line by tapping its card. */
  | { readonly kind: "line"; readonly unit: ResolvedUnit }
  | {
      readonly kind: "qty";
      readonly unit: ResolvedUnit;
      readonly lineId: string;
      readonly qty: string;
    };

type DoorAction = "pick" | "unpick" | "pack" | "ship";

interface DoorFailure {
  readonly action: DoorAction;
  readonly failure: OutboxFailure;
  /** The pick body the refusal answered, so the override can re-send it. */
  readonly body: PickBody | undefined;
}

// ---------------------------------------------------------------------------
// Pure helpers — exported so the arithmetic and the matching are tested as
// tables, never only through a render.
// ---------------------------------------------------------------------------

/** The open shipment of an order out of a newest-first list, if any. */
export function openShipmentOf(items: readonly ShipmentSummary[]): ShipmentSummary | undefined {
  return items.find((item) => OPEN_STATUSES.has(item.status));
}

/** A scaled 6-dp integer back to the wire's measure text ("1200.000000"). */
export function scaledToMeasureText(scaled: bigint): string {
  const abs = scaled < 0n ? -scaled : scaled;
  const whole = abs / SCALE;
  const frac = (abs % SCALE).toString().padStart(6, "0");
  return `${scaled < 0n ? "-" : ""}${whole.toString()}.${frac}`;
}

/**
 * A scaled 6-dp integer as a 3-dp `SalesQty` editing value ("1200", "400.5"),
 * or `undefined` when it does not fit the line's frame (a 6-dp measure with
 * digits below the thousandth cannot be a pick quantity — the server would
 * refuse the text, so the screen refuses to offer it).
 */
export function scaledToQtyText(scaled: bigint): string | undefined {
  if (scaled < 0n) return undefined;
  if (scaled % 1000n !== 0n) return undefined;
  const whole = scaled / SCALE;
  const milli = (scaled % SCALE) / 1000n;
  if (milli === 0n) return whole.toString();
  return `${whole.toString()}.${milli.toString().padStart(3, "0").replace(/0+$/u, "")}`;
}

/**
 * A scaled 6-dp integer in the LINE's three-decimal WIRE frame ("400.000",
 * "0.000") — what a `data-*` attribute carries. An attribute is read by a
 * machine (the walkthroughs pin it as a value), so it speaks the wire's frame
 * and never the editing value's trimmed one, which is for a person's eyes and
 * a keypad. A negative is a line the device's own queue has over-netted: it
 * reads as nothing open. Digits below the thousandth cannot occur on a line
 * quantity; if they ever do, the full measure text is the honest answer.
 */
export function scaledToWireQty(scaled: bigint): string {
  if (scaled <= 0n) return "0.000";
  if (scaled % 1000n !== 0n) return scaledToMeasureText(scaled);
  const whole = scaled / SCALE;
  const milli = (scaled % SCALE) / 1000n;
  return `${whole.toString()}.${milli.toString().padStart(3, "0")}`;
}

/** "400.000" → "400", "400.500" → "400.5" — the wire text as an editing value. */
export function trimQty(text: string): string {
  const scaled = parseMeasure(text);
  if (scaled === undefined) return text;
  return scaledToQtyText(scaled) ?? text;
}

/** The queued picks of ONE shipment, read off the device queue. */
export function queuedPicksOf(items: readonly OutboxItem[], shipmentId: string): QueuedPick[] {
  const out: QueuedPick[] = [];
  for (const item of items) {
    if (item.op.kind !== "shipment_pick" || item.op.shipmentId !== shipmentId) continue;
    out.push({
      lineId: item.op.body.sales_order_line_id,
      unitId: item.op.body.stock_unit_id,
      qty: item.op.body.qty_picked,
    });
  }
  return out;
}

/** Is a PACK of this shipment still waiting on the device? Its delivery note
 * is drawn when the pack reaches the server, so until then there is none. */
export function packQueuedFor(items: readonly OutboxItem[], shipmentId: string): boolean {
  return items.some(
    (item) => item.op.kind === "shipment_pack" && item.op.shipmentId === shipmentId,
  );
}

/**
 * What the delivery-note slot says, as one pure decision (D-288):
 *   * `none`    — nothing to say: still picking with no pack queued, or the
 *                 shipment was cancelled with no note (its door refuses one).
 *   * `queued`  — picking, with this device's pack waiting to go.
 *   * `offline` — there is a note (or a draw) to reach and no signal to
 *                 reach it with.
 *   * `link`    — a note is on file; open it. On ANY status past picking: a
 *                 shipment cancelled after its note was drawn still serves
 *                 it, and the door answers for it.
 *   * `retry`   — packed or later with no note on file, and the draw is
 *                 offered. NOT necessarily a failed draw: a shipment packed
 *                 before notes existed has none either, so the slot's words
 *                 are neutral ("no delivery note on file").
 */
export type NoteSlot = "none" | "queued" | "offline" | "link" | "retry";

export function noteSlotOf(
  shipment: { readonly status: string; readonly delivery_note_file_id: string | null },
  packQueued: boolean,
  reachable: boolean,
): NoteSlot {
  if (shipment.status === "picking") return packQueued ? "queued" : "none";
  if (shipment.delivery_note_file_id !== null) return reachable ? "link" : "offline";
  if (!NOTE_STATUSES.has(shipment.status)) return "none";
  return reachable ? "retry" : "offline";
}

/** Whether a queued op is one of ONE order's shipment ops. */
export function isShipmentOpOf(item: OutboxItem, orderId: string): boolean {
  const op: FloorOp = item.op;
  return (
    (op.kind === "shipment_open" ||
      op.kind === "shipment_pick" ||
      op.kind === "shipment_unpick" ||
      op.kind === "shipment_pack" ||
      op.kind === "shipment_ship") &&
    op.orderId === orderId
  );
}

/** Every shipment op of ONE order still waiting on the device. */
export function pendingShipmentOps(items: readonly OutboxItem[], orderId: string): number {
  let n = 0;
  for (const item of items) if (isShipmentOpOf(item, orderId)) n += 1;
  return n;
}

/**
 * The kinds of ONE order's shipment ops that left the pending queue without
 * being parked between two looks at the queue, i.e. the ops the server took.
 * `before` maps each op key in flight at the earlier look to its kind. A parked
 * op (needs attention, needs review, foreign) changed nothing on the server,
 * so it is not counted, the rule the count worksheet keeps (floor-count.tsx).
 */
export function drainedShipmentOps(
  before: ReadonlyMap<string, FloorOp["kind"]>,
  pending: readonly OutboxItem[],
  parked: readonly OutboxItem[],
  orderId: string,
): FloorOp["kind"][] {
  const still = new Set<string>();
  for (const item of pending) if (isShipmentOpOf(item, orderId)) still.add(item.clientKey);
  const held = new Set<string>();
  for (const item of parked) if (isShipmentOpOf(item, orderId)) held.add(item.clientKey);
  const drained: FloorOp["kind"][] = [];
  for (const [key, kind] of before) if (!still.has(key) && !held.has(key)) drained.push(kind);
  return drained;
}

/**
 * A line's open quantity AFTER this device's queued picks, scaled. The server's
 * `qty_open` already nets the picks it holds; the device nets its own on top,
 * clamped at zero, so a line reads closed the moment the last queued pick is
 * confirmed and not fifteen minutes later when the queue drains.
 */
export function openAfterQueued(line: PickPlanLine, queued: readonly QueuedPick[]): bigint {
  let open = parseMeasure(line.qty_open) ?? 0n;
  for (const pick of queued) {
    if (pick.lineId !== line.sales_order_line_id) continue;
    open -= parseMeasure(pick.qty) ?? 0n;
  }
  return open > 0n ? open : 0n;
}

export function isPickable(line: PickPlanLine, queued: readonly QueuedPick[]): boolean {
  return PICKABLE_LINE_STATUSES.has(line.line_status) && openAfterQueued(line, queued) > 0n;
}

/**
 * Match a scanned payload against the plan LOCALLY (the receive screen's
 * `ScanTick` rule — no round trip, works with no signal): the unit's serial,
 * or a canonical `/q/su/{uuid}` payload carrying the unit id (§9.9 — the
 * client never RESOLVES a stock payload; matching an id the plan already holds
 * is not resolution). Lines that can still take a pick are searched first, so
 * a unit planned on a closed line falls through to the wire path.
 */
export function findPlannedUnit(
  plan: readonly PickPlanLine[],
  payload: string,
  queued: readonly QueuedPick[],
): { line: PickPlanLine; unit: PickPlanUnit } | undefined {
  const needle = payload.trim().toLowerCase();
  if (needle.length === 0) return undefined;
  const matches = (unit: PickPlanUnit): boolean =>
    unit.serial_code.toLowerCase() === needle || needle.includes(unit.stock_unit_id.toLowerCase());
  for (const line of plan) {
    if (!isPickable(line, queued)) continue;
    const unit = line.reserved_units.find(matches);
    if (unit !== undefined) return { line, unit };
  }
  return undefined;
}

/**
 * The pre-press disclosure: TRUE when at least one pick exists (a server row
 * or a queued one) and no non-cancelled line has anything left open — the
 * shipment that ships now leaves every line `shipped`, and
 * `orderStatusAfterShip` on the server will answer `shipped` for the order.
 */
export function allOpenLinesClosed(
  plan: readonly PickPlanLine[],
  queued: readonly QueuedPick[],
  hasPicks: boolean,
): boolean {
  if (!hasPicks) return false;
  if (plan.length === 0) return false;
  return plan.every(
    (line) => !PICKABLE_LINE_STATUSES.has(line.line_status) || openAfterQueued(line, queued) === 0n,
  );
}

/**
 * The unit's remainder after a pick of `qty` in the LINE's UoM — scaled, or
 * `undefined` when the two frames differ (no honest conversion on the client)
 * or the unit's measure is unknown. A NEGATIVE result is returned as is: the
 * caller decides whether that is a disabled Confirm or nothing to say.
 */
export function remainderAfterPick(
  unit: ResolvedUnit,
  qty: string,
  lineUom: string,
): bigint | undefined {
  if (unit.measureUom === null || unit.measureUom !== lineUom) return undefined;
  const remaining = parseMeasure(unit.remainingMeasure);
  const picked = parseMeasure(qty);
  if (remaining === undefined || picked === undefined) return undefined;
  return remaining - picked;
}

function resolvedFromPlan(unit: PickPlanUnit): ResolvedUnit {
  return {
    stockUnitId: unit.stock_unit_id,
    serialCode: unit.serial_code,
    locationCode: unit.location_code,
    remainingMeasure: unit.remaining_measure,
    measureUom: unit.measure_uom,
    planned: true,
  };
}

/**
 * The most likely pick, pre-filled: the line's open quantity, capped at the
 * unit's remaining when the frames agree and the unit holds less. Typing
 * replaces it (the numpad's calculator freshness), so the pre-fill costs no
 * tap when it is right and none when it is wrong.
 */
function defaultQtyFor(
  line: PickPlanLine,
  unit: ResolvedUnit,
  queued: readonly QueuedPick[],
): string {
  const open = openAfterQueued(line, queued);
  const remaining =
    unit.measureUom !== null && unit.measureUom === line.uom_code
      ? parseMeasure(unit.remainingMeasure)
      : undefined;
  const pick = remaining !== undefined && remaining < open ? remaining : open;
  return scaledToQtyText(pick) ?? trimQty(line.qty_open);
}

/** The order number off the queue's own cached read — so the title and the
 * open's tray identity can name the order before its detail has loaded. */
function cachedOrderNo(orderId: string): string | undefined {
  const cached = loadRead<OrderListResponse>(SHIP_QUEUE_CACHE_KEY);
  return cached?.value.items.find((item) => item.id === orderId)?.order_no;
}

function lineStatusWord(t: (key: string) => string, status: PickPlanLine["line_status"]): string {
  // Literal keys per member (the order detail's own family), so the parity
  // gate sees each one; a member this switch does not know renders the wire
  // token, degraded visibly, never blank.
  switch (status) {
    case "open":
      return t("sales.orders.detail.lineStatus.open");
    case "in_production":
      return t("sales.orders.detail.lineStatus.in_production");
    case "ready":
      return t("sales.orders.detail.lineStatus.ready");
    case "shipped":
      return t("sales.orders.detail.lineStatus.shipped");
    case "cancelled":
      return t("sales.orders.detail.lineStatus.cancelled");
    default:
      return status;
  }
}

function shipmentStatusWord(
  t: (key: string) => string,
  status: ShipmentDetailResponse["status"],
): string {
  switch (status) {
    case "picking":
      return t("sales.shipments.status.picking");
    case "packed":
      return t("sales.shipments.status.packed");
    case "shipped":
      return t("sales.shipments.status.shipped");
    case "delivered":
      return t("sales.shipments.status.delivered");
    case "cancelled":
      return t("sales.shipments.status.cancelled");
    default:
      return status;
  }
}

// ---------------------------------------------------------------------------
// The screen
// ---------------------------------------------------------------------------

export function FloorShipScreen() {
  const { t, i18n } = useTranslation();
  const { orderId } = useParams({ from: "/floor/ship/$orderId" });
  const perms = useInventoryPermissions();
  const outbox = useFloorOutbox();
  const online = useOnline();
  const queryClient = useQueryClient();
  const { state, submit } = useFloorSubmit();
  const locale = i18n.language;

  // Ruling 7: the override is an fa/ga lineage's, read on the lineage the
  // service re-checks (`shipments.service.ts` ADMIN_LINEAGES, D-224, D-586).
  const isAdmin = hasLineage(perms, ["facility_admin", "group_admin"]);

  // ---- which shipment ------------------------------------------------------
  const [opened, setOpened] = useState<ShipmentDetailResponse | undefined>(undefined);
  const [openFailure, setOpenFailure] = useState<OutboxFailure | undefined>(undefined);
  const [openQueued, setOpenQueued] = useState(false);
  const openAttempted = useRef(false);

  const openRead = useCachedRead<ShipmentListResponse>({
    queryKey: ["floor", "ship", "open", orderId],
    cacheKey: readCacheKey("ship-open", orderId),
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/shipments", {
          params: { query: { sales_order_id: orderId, limit: 10 } },
          signal: floorReadSignal(signal),
        }),
        "shipments",
      ),
  });
  const listed = openRead.data === undefined ? undefined : openShipmentOf(openRead.data.items);
  const shipmentId = opened?.id ?? listed?.id;

  const detailRead = useCachedRead<ShipmentDetailResponse>({
    queryKey: ["floor", "shipment", shipmentId ?? "none"],
    cacheKey: readCacheKey("shipment", shipmentId ?? "none"),
    enabled: shipmentId !== undefined,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/shipments/{id}", {
          params: { path: { id: shipmentId ?? "" } },
          signal: floorReadSignal(signal),
        }),
        "shipment",
      ),
  });
  const detail = detailRead.data;

  // A DRAINED op re-reads the shipment. A tap this screen submits applies the
  // shipment the door answers (`applyDetail`), but an op queued before the tab
  // was closed or killed drains after the reload with nobody awaiting it, and
  // its answer is applied nowhere. On a slow device the reload's read can land
  // before that drain, and the screen then shows the pre-drain shipment for
  // good (M09-S08 found it under load: one picked row where the server held
  // two). What the screen can always observe is one of this order's ops
  // leaving the pending queue without being parked, which is "the server took
  // it", so that is when the shipment is read again. The order's open list is
  // read again only when the drained op was the OPEN, because that is the one
  // op that changes which shipment the screen works on. Any other op leaves the
  // list alone: after a ship the list no longer holds the shipment, so a re-read
  // would empty the screen's shipment and fire the open on entry against an
  // order that has nothing left to pick (staging found it on 78a005ba, M09-S09:
  // "Could not start picking" where the shipped panel belonged). `refreshQuery`
  // (cancel, then invalidate) and not a bare invalidate, for the reason
  // floor-count.tsx gives: an invalidate over an in-flight first fetch
  // coalesces instead of refetching.
  const inFlightShipmentOps = useMemo(
    () =>
      new Map(
        outbox.pending
          .filter((item) => isShipmentOpOf(item, orderId))
          .map((item) => [item.clientKey, item.op.kind] as const),
      ),
    [outbox.pending, orderId],
  );
  const wasInFlight = useRef<ReadonlyMap<string, FloorOp["kind"]>>(inFlightShipmentOps);
  useEffect(() => {
    const before = wasInFlight.current;
    wasInFlight.current = inFlightShipmentOps;
    const drained = drainedShipmentOps(
      before,
      outbox.pending,
      [...outbox.needsAttention, ...outbox.needsReview, ...outbox.foreign],
      orderId,
    );
    if (drained.length === 0) return;
    if (drained.includes("shipment_open")) {
      void refreshQuery(queryClient, ["floor", "ship", "open", orderId]);
    }
    if (shipmentId !== undefined) void refreshQuery(queryClient, ["floor", "shipment", shipmentId]);
  }, [
    inFlightShipmentOps,
    outbox.pending,
    outbox.needsAttention,
    outbox.needsReview,
    outbox.foreign,
    orderId,
    shipmentId,
    queryClient,
  ]);

  /** Every door answers the shipment itself: the response IS the next read. */
  const applyDetail = useCallback(
    (next: ShipmentDetailResponse): void => {
      queryClient.setQueryData(["floor", "shipment", next.id], next);
      saveRead(readCacheKey("shipment", next.id), next);
    },
    [queryClient],
  );

  const startPicking = useCallback(async (): Promise<void> => {
    setOpenFailure(undefined);
    setOpenQueued(false);
    const settled = await submit({ kind: "shipment_open", orderId, body: {} }, undefined, {
      identity: cachedOrderNo(orderId),
    });
    if (settled === undefined) {
      floorFeedback("error");
      return;
    }
    if (settled.outcome === "applied") {
      const next = settled.response as ShipmentDetailResponse;
      applyDetail(next);
      setOpened(next);
      floorFeedback("ok");
      return;
    }
    if (settled.outcome === "queued") {
      setOpenQueued(true);
      floorFeedback("warn");
      return;
    }
    if (settled.outcome === "needs_attention") setOpenFailure(settled.failure);
    floorFeedback("error");
  }, [applyDetail, orderId, submit]);

  // The open on entry — ONCE, and only off a LIVE list that says no shipment is
  // open (a cached list may predate one opened from another tablet; the live
  // read is what proves the absence). A refused open is not re-fired: the
  // "Start picking" press under the error card is the retry.
  useEffect(() => {
    if (shipmentId !== undefined || openAttempted.current) return;
    if (openRead.source !== "live" || !online) return;
    openAttempted.current = true;
    void startPicking();
  }, [shipmentId, openRead.source, online, startPicking]);

  // ---- the pick flow -------------------------------------------------------
  const [stage, setStage] = useState<Stage>({ kind: "scan" });
  const [scanMiss, setScanMiss] = useState<
    { readonly payload: string; readonly needsSignal: boolean } | undefined
  >(undefined);
  const [lastFailure, setLastFailure] = useState<DoorFailure | undefined>(undefined);
  const [queuedNote, setQueuedNote] = useState(false);
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  // The delivery note's draw: in flight, and the last refusal it met.
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteFailure, setNoteFailure] = useState<OutboxFailure | undefined>(undefined);

  const queued = useMemo(
    () => (shipmentId === undefined ? [] : queuedPicksOf(outbox.pending, shipmentId)),
    [outbox.pending, shipmentId],
  );
  const pendingCount = pendingShipmentOps(outbox.pending, orderId);
  const plan = useMemo(() => detail?.pick_plan ?? [], [detail]);
  const liveLines = useMemo(
    () => (detail?.lines ?? []).filter((row) => row.undone_at === null),
    [detail],
  );
  const closesOrder =
    detail !== undefined &&
    OPEN_STATUSES.has(detail.status) &&
    allOpenLinesClosed(plan, queued, liveLines.length > 0 || queued.length > 0);
  const busy = state.phase === "submitting";
  // "Can this device reach us" is the OUTBOX's answer, not the browser's: an
  // access point with no backhaul says online while nothing goes through
  // (floor-chrome.tsx's `syncTone`).
  const tone = syncTone(outbox, online);
  const reachable = tone !== "offline" && tone !== "unreachable";
  const noteSlot =
    detail === undefined
      ? "none"
      : noteSlotOf(detail, packQueuedFor(outbox.pending, detail.id), reachable);

  function enterQty(unit: ResolvedUnit, line: PickPlanLine): void {
    setScanMiss(undefined);
    setLastFailure(undefined);
    setQueuedNote(false);
    setStage({
      kind: "qty",
      unit,
      lineId: line.sales_order_line_id,
      qty: defaultQtyFor(line, unit, queued),
    });
  }

  /** A scan, a wedge burst, a typed serial or a tap on a planned unit's row. */
  async function onScan(payload: string): Promise<void> {
    if (detail === undefined) return;
    const planned = findPlannedUnit(plan, payload, queued);
    if (planned !== undefined) {
      enterQty(resolvedFromPlan(planned.unit), planned.line);
      floorFeedback("ok");
      return;
    }
    // Not in the plan. Resolution is the SERVER's (§9.9), through the existing
    // `scan` op — online only: a scan queued into a dead zone would resolve
    // hours later to a screen that is gone.
    if (!online) {
      setScanMiss({ payload, needsSignal: true });
      floorFeedback("error");
      return;
    }
    setScanMiss(undefined);
    const settled = await submit(
      {
        kind: "scan",
        body: {
          scanned_payload: payload,
          action: "lookup",
          app_area: "floor",
          device_id: deviceId(),
        },
      },
      undefined,
      { identity: payload },
    );
    const response =
      settled?.outcome === "applied" ? (settled.response as ScanResponse) : undefined;
    const unit =
      response?.resolved_kind === "stock_unit" ? (response.stock_unit ?? undefined) : undefined;
    if (unit !== undefined) {
      const resolved: ResolvedUnit = {
        stockUnitId: unit.id,
        serialCode: unit.serial_code,
        locationCode: unit.location_path ?? undefined,
        remainingMeasure: unit.remaining_measure,
        measureUom: unit.measure_uom,
        planned: false,
      };
      const pickable = plan.filter((line) => isPickable(line, queued));
      const only = pickable[0];
      if (pickable.length === 1 && only !== undefined) enterQty(resolved, only);
      else setStage({ kind: "line", unit: resolved });
      floorFeedback("ok");
      return;
    }
    setScanMiss({ payload, needsSignal: settled?.outcome === "queued" });
    floorFeedback("error");
  }

  /** One door, one settlement, one haptic — the shape every door below uses. */
  async function runDoor(
    op: FloorOp,
    identity: string,
    action: DoorAction,
    body: PickBody | undefined,
  ): Promise<OutboxSettlement | undefined> {
    setLastFailure(undefined);
    setQueuedNote(false);
    const settled = await submit(op, undefined, { identity });
    if (settled === undefined) {
      floorFeedback("error");
      return undefined;
    }
    if (settled.outcome === "applied") {
      applyDetail(settled.response as ShipmentDetailResponse);
      floorFeedback("ok");
    } else if (settled.outcome === "queued") {
      setQueuedNote(true);
      floorFeedback("warn");
    } else {
      if (settled.outcome === "needs_attention") {
        setLastFailure({ action, failure: settled.failure, body });
      }
      floorFeedback("error");
    }
    return settled;
  }

  async function confirmPick(override: boolean): Promise<void> {
    if (detail === undefined || stage.kind !== "qty") return;
    const line = plan.find((candidate) => candidate.sales_order_line_id === stage.lineId);
    if (line === undefined) return;
    // THE WIRE'S FRAME, not the keypad's: the operator types "200" and the
    // request says "200.000" — the text the row, the audit chain, the event and
    // every later read carry. The queued op's body is also what the tray and
    // the net-of-queue arithmetic read, so one frame serves all of them. A
    // value the screen cannot parse is sent as typed and the server refuses it.
    const typed = normalizeQty(stage.qty);
    const typedScaled = parseMeasure(typed);
    const qtyText =
      typedScaled !== undefined && typedScaled > 0n && typedScaled % 1000n === 0n
        ? scaledToWireQty(typedScaled)
        : typed;
    const body: PickBody = {
      stock_unit_id: stage.unit.stockUnitId,
      sales_order_line_id: line.sales_order_line_id,
      qty_picked: qtyText,
      ...(override ? { override: true } : {}),
    };
    const settled = await runDoor(
      { kind: "shipment_pick", orderId, shipmentId: detail.id, body },
      `${detail.shipment_no} · ${stage.unit.serialCode} · ${formatMeasure(qtyText, { locale })} ${unitSymbol(line.uom_code, { locale, count: Number(qtyText) })}`,
      "pick",
      body,
    );
    // Applied or queued: the row is on the server or on this device — back to
    // the scanner for the next bobbin. Refused: stay, so the override (or a
    // corrected length) is one tap away from the card that explains it.
    if (settled?.outcome === "applied" || settled?.outcome === "queued") setStage({ kind: "scan" });
  }

  async function unpick(row: ShipmentLine): Promise<void> {
    if (detail === undefined) return;
    await runDoor(
      { kind: "shipment_unpick", orderId, shipmentId: detail.id, lineId: row.id },
      `${detail.shipment_no} · ${row.serial_code}`,
      "unpick",
      undefined,
    );
  }

  async function pack(): Promise<void> {
    if (detail === undefined) return;
    await runDoor(
      { kind: "shipment_pack", orderId, shipmentId: detail.id },
      detail.shipment_no,
      "pack",
      undefined,
    );
  }

  async function ship(): Promise<void> {
    if (detail === undefined) return;
    const body: ShipBody = {
      ...(carrier.trim().length > 0 ? { carrier: carrier.trim() } : {}),
      ...(tracking.trim().length > 0 ? { tracking_ref: tracking.trim() } : {}),
    };
    await runDoor(
      { kind: "shipment_ship", orderId, shipmentId: detail.id, body },
      detail.shipment_no,
      "ship",
      undefined,
    );
  }

  /** Draw the note the pack could not (D-288 ruling 11). Online only, never
   * queued (the header says why); the answer is the shipment itself, so it
   * replaces the read exactly as every door above does. */
  async function redrawNote(): Promise<void> {
    if (detail === undefined) return;
    setNoteBusy(true);
    setNoteFailure(undefined);
    try {
      const next = unwrap(
        await client.POST("/v1/shipments/{id}/delivery-note", {
          params: {
            path: { id: detail.id },
            header: { "Idempotency-Key": newIdempotencyKey() },
          },
        }),
        "delivery-note",
      );
      applyDetail(next);
      floorFeedback("ok");
    } catch (error) {
      setNoteFailure(describeFailure(error));
      floorFeedback("error");
    } finally {
      setNoteBusy(false);
    }
  }

  /** The delivery-note slot, rendered where the shipment's state puts it: in
   * the packed panel, in the shipped card, or under the picks while a pack is
   * queued. `noteSlot` decides; this only draws it. */
  function noteBlock(): React.ReactNode {
    switch (noteSlot) {
      case "none":
        return null;
      case "queued":
        return (
          <p className="floor-hint floor-hint-warn" data-testid="floor-ship-delivery-note-queued">
            {t("sales.floor.ship.deliveryNoteQueued")}
          </p>
        );
      case "offline":
        return (
          <p className="floor-hint floor-hint-warn" data-testid="floor-ship-delivery-note-offline">
            {t("sales.floor.ship.deliveryNoteOffline")}
          </p>
        );
      case "link":
        return detail === undefined ? null : (
          <a
            className="floor-go"
            href={deliveryNotePath(detail.id)}
            target="_blank"
            rel="noreferrer"
            data-testid="floor-ship-delivery-note"
          >
            {t("sales.floor.ship.deliveryNote")}
          </a>
        );
      // No note on file: a neutral card, not an alert — a shipment packed
      // before notes existed reads exactly like a draw that failed at pack,
      // and neither is news that interrupts the operator. Only a refused DRAW
      // is announced (the error line's own `role="alert"`). The testid keeps
      // its PR-D4 name so the walkthroughs and the spec's registry hold.
      case "retry":
        return (
          <div
            className={
              noteFailure === undefined
                ? "floor-result-card"
                : "floor-result-card floor-result-warn"
            }
            data-testid="floor-ship-delivery-note-failed"
            data-code={noteFailure?.code ?? ""}
          >
            <p className="floor-result-body">{t("sales.floor.ship.deliveryNoteNone")}</p>
            {noteFailure !== undefined ? (
              <p
                className="floor-result-body"
                role="alert"
                data-testid="floor-ship-delivery-note-error"
              >
                {floorFailureMessage(t, noteFailure, "inventory.errors.generic", locale)}
              </p>
            ) : null}
            {noteFailure?.requestId !== undefined ? (
              <p className="floor-state-rid">
                {t("inventory.common.requestId")}: <code>{noteFailure.requestId}</code>
              </p>
            ) : null}
            <FloorButton
              variant="primary"
              disabled={noteBusy}
              onClick={() => void redrawNote()}
              testId="floor-ship-delivery-note-retry"
            >
              {noteBusy
                ? t("sales.floor.ship.deliveryNoteDrawing")
                : t("sales.floor.ship.deliveryNoteDraw")}
            </FloorButton>
          </div>
        );
    }
  }

  // ---- derived for the quantity panel --------------------------------------
  const qtyLine =
    stage.kind === "qty"
      ? plan.find((candidate) => candidate.sales_order_line_id === stage.lineId)
      : undefined;
  const qtyText = stage.kind === "qty" ? normalizeQty(stage.qty) : "";
  const qtyScaled = stage.kind === "qty" ? parseMeasure(qtyText) : undefined;
  const lineOpen = qtyLine === undefined ? 0n : openAfterQueued(qtyLine, queued);
  const remainderAfter =
    stage.kind === "qty" && qtyLine !== undefined
      ? remainderAfterPick(stage.unit, qtyText, qtyLine.uom_code)
      : undefined;
  const qtyInvalid = qtyScaled === undefined || qtyScaled <= 0n || !SALES_QTY_RE.test(qtyText);
  // `qtyInvalid` already established that `qtyScaled` is a number (TS narrows
  // through the aliased condition), so a second `!== undefined` here is dead.
  const overLine = !qtyInvalid && qtyScaled > lineOpen;
  const overUnit = !qtyInvalid && remainderAfter !== undefined && remainderAfter < 0n;
  const confirmReason =
    qtyLine === undefined
      ? undefined
      : qtyInvalid
        ? t("sales.floor.ship.qtyInvalid")
        : overLine
          ? t("sales.floor.ship.exceedsLine", {
              remaining: formatMeasure(scaledToMeasureText(lineOpen), { locale }),
              uom: unitSymbol(qtyLine.uom_code, { locale }),
            })
          : overUnit && stage.kind === "qty"
            ? t("sales.floor.ship.exceedsUnit", {
                remaining: formatMeasure(stage.unit.remainingMeasure, { locale }),
                uom: unitSymbol(stage.unit.measureUom ?? "", { locale }),
              })
            : undefined;
  const canOfferFullUnit =
    stage.kind === "qty" &&
    stage.unit.measureUom === qtyLine?.uom_code &&
    (() => {
      const remaining = parseMeasure(stage.unit.remainingMeasure);
      return (
        remaining !== undefined &&
        remaining > 0n &&
        remaining <= lineOpen &&
        scaledToQtyText(remaining) !== undefined
      );
    })();

  const overrideOffered =
    isAdmin &&
    stage.kind === "qty" &&
    lastFailure?.action === "pick" &&
    lastFailure.failure.code === "UNIT_NOT_RESERVED" &&
    lastFailure.failure.extensions["reason"] === "not_reserved";

  const orderNo = detail?.order_no ?? cachedOrderNo(orderId);
  const title =
    orderNo !== undefined
      ? t("sales.floor.ship.title", { orderNo })
      : t("sales.floor.ship.queueTitle");

  // ---- the action bar: ONE primary action per stage ------------------------
  let actions: React.ReactNode = undefined;
  if (detail !== undefined) {
    if (stage.kind === "qty") {
      actions = (
        <>
          <FloorButton
            variant="primary"
            disabled={busy || confirmReason !== undefined}
            disabledReason={confirmReason}
            onClick={() => void confirmPick(false)}
            testId="floor-ship-confirm"
          >
            {t("sales.floor.ship.confirm")}
          </FloorButton>
          <FloorButton
            variant="ghost"
            onClick={() => {
              setStage({ kind: "scan" });
              setLastFailure(undefined);
            }}
            testId="floor-ship-cancel-qty"
          >
            {t("inventory.common.cancel")}
          </FloorButton>
        </>
      );
    } else if (detail.status === "picking" && liveLines.length > 0 && stage.kind === "scan") {
      // Pack is rendered only where the press is honoured (a packed empty box
      // is 409 SHIPMENT_EMPTY — so no live line, no button; D-088).
      actions = (
        <FloorButton
          variant="primary"
          disabled={busy}
          onClick={() => void pack()}
          testId="floor-ship-pack"
        >
          {t("sales.floor.ship.pack")}
        </FloorButton>
      );
    } else if (detail.status === "packed") {
      actions = (
        <FloorButton
          variant="primary"
          disabled={busy}
          onClick={() => void ship()}
          testId="floor-ship-ship"
        >
          {t("sales.floor.ship.ship")}
        </FloorButton>
      );
    }
  }

  return (
    <FloorScreen
      title={title}
      back={
        <Link to="/floor/ship" className="floor-back">
          {t("inventory.floor.back")}
        </Link>
      }
      actions={actions}
    >
      <div data-testid="floor-ship-screen" data-shipment-id={detail?.id ?? ""}>
        <SnapshotNote
          source={detailRead.source}
          cachedAt={detailRead.cachedAt}
          revalidating={detailRead.revalidating}
        />

        {/* §4.8's offline banner: this ORDER's picks the device still holds.
            The chrome's `floor-pending-count` is the whole queue; this is the
            slice that belongs to the bobbins in front of the operator. */}
        {pendingCount > 0 || !online ? (
          <p className="floor-ship-offline" role="status" data-testid="floor-ship-offline">
            {pendingCount > 0
              ? t("sales.floor.ship.pending", { count: pendingCount })
              : t("inventory.floor.sync.offline")}
          </p>
        ) : null}

        {(openRead.isLoading || detailRead.isLoading) && detail === undefined ? (
          <FloorSkeleton rows={3} />
        ) : null}

        {/* No shipment and no way to open one: the list read failed online, or
            the device is in a dead zone with nothing cached. Both are stated
            as what they are — never as "nothing to pick". */}
        {detail === undefined && shipmentId === undefined && !openRead.isLoading ? (
          openRead.source === "none" ? (
            openRead.online ? (
              /* A 403 is the ROLE's answer, not the network's: no Retry
                 beside it (lib/floor-errors.ts's `floorForbidden`), because
                 this route is reachable by URL from a tablet whose hub
                 correctly shows no ship tile. */
              <FloorError
                title={t("sales.floor.ship.loadFailedTitle")}
                detail={
                  floorForbidden(openRead.error)
                    ? t("inventory.floor.tray.forbidden")
                    : t("sales.floor.ship.loadFailedBody")
                }
                requestId={floorRequestId(openRead.error)}
                {...(floorForbidden(openRead.error) ? {} : { onRetry: openRead.refetch })}
              />
            ) : (
              <FloorNeedsConnection what={t("sales.floor.ship.thisOrder")} />
            )
          ) : !online ? (
            <FloorNeedsConnection what={t("sales.floor.ship.thisOrder")} />
          ) : null
        ) : null}

        {openFailure !== undefined ? (
          <div
            className="floor-result-card floor-result-warn"
            role="alert"
            data-testid="floor-ship-error"
            data-code={openFailure.code ?? ""}
          >
            <p className="floor-result-title">{t("sales.floor.ship.openFailedTitle")}</p>
            <p className="floor-result-body">
              {floorFailureMessage(t, openFailure, "inventory.errors.generic", locale)}
            </p>
            {openFailure.requestId !== undefined ? (
              <p className="floor-state-rid">
                {t("inventory.common.requestId")}: <code>{openFailure.requestId}</code>
              </p>
            ) : null}
            <FloorButton
              variant="primary"
              disabled={busy}
              onClick={() => void startPicking()}
              testId="floor-ship-open"
            >
              {t("sales.floor.ship.open")}
            </FloorButton>
          </div>
        ) : null}

        {openQueued && detail === undefined ? (
          <p className="floor-hint floor-hint-warn" data-testid="floor-ship-open-queued">
            {t("sales.floor.ship.openNeedsSignal")}
          </p>
        ) : null}

        {detail !== undefined ? (
          <>
            {/* The shipment that has LEFT: the one fact the screen states now.
                Nothing on it is a control any more. */}
            {detail.status === "shipped" ||
            detail.status === "delivered" ||
            detail.status === "cancelled" ? (
              <div
                className="floor-result-card floor-result-ok floor-ship-done"
                role="status"
                data-testid="floor-ship-done"
                data-status={detail.status}
              >
                <p className="floor-result-title">
                  {detail.shipment_no} · {shipmentStatusWord(t, detail.status)}
                </p>
                <p className="floor-result-body">{t("sales.floor.ship.doneBody")}</p>
                {/* The note travels with the goods, so it stays reachable after
                    Ship replaced the packed panel it was in. A cancelled
                    shipment shows its note only if one was drawn before the
                    cancel (`noteSlotOf`); otherwise nothing. */}
                {noteBlock()}
              </div>
            ) : null}

            {lastFailure !== undefined ? (
              <div
                className="floor-result-card floor-result-warn"
                role="alert"
                data-testid="floor-ship-error"
                data-code={lastFailure.failure.code ?? ""}
                data-action={lastFailure.action}
              >
                <p className="floor-result-title">{t("sales.floor.ship.failedTitle")}</p>
                <p className="floor-result-body">
                  {floorFailureMessage(t, lastFailure.failure, "inventory.errors.generic", locale)}
                </p>
                {lastFailure.failure.requestId !== undefined ? (
                  <p className="floor-state-rid">
                    {t("inventory.common.requestId")}: <code>{lastFailure.failure.requestId}</code>
                  </p>
                ) : null}
                {overrideOffered ? (
                  <FloorButton
                    variant="danger"
                    disabled={busy}
                    onClick={() => void confirmPick(true)}
                    testId="floor-ship-override"
                  >
                    {t("sales.floor.ship.override")}
                  </FloorButton>
                ) : null}
              </div>
            ) : null}

            {queuedNote ? (
              <p className="floor-result-queued" role="status" data-testid="floor-ship-queued">
                {t("inventory.floor.scan.queuedTitle")}
              </p>
            ) : null}

            {/* THE SCAN, first on the screen (the receive screen's rule: the
                primary motion sits above the list, so a good scan and a
                scanner that never fired are tellable apart without scrolling).
                The wrapper carries the testid; the shared ScanInput's own field
                is `floor-scan-payload` inside it. */}
            {detail.status === "picking" && stage.kind === "scan" ? (
              <section className="floor-panel" data-testid="floor-ship-scan">
                <h2 className="floor-panel-head">{t("sales.floor.ship.scan")}</h2>
                <ScanInput
                  autoFocus
                  captureWedge
                  busy={busy}
                  label={t("inventory.floor.scan.inputLabel")}
                  placeholder={t("inventory.floor.scan.inputPlaceholder")}
                  onScan={(payload) => {
                    void onScan(payload);
                  }}
                />
                {scanMiss !== undefined ? (
                  <p className="floor-scan-error" role="alert" data-testid="floor-ship-scan-miss">
                    {scanMiss.needsSignal
                      ? t("sales.floor.ship.scanNeedsSignal")
                      : t("sales.floor.ship.scanNoMatch", { payload: scanMiss.payload })}
                  </p>
                ) : null}
              </section>
            ) : null}

            {stage.kind === "line" ? (
              <p className="floor-hint floor-hint-warn" data-testid="floor-ship-choose-line">
                {t("sales.floor.ship.chooseLine")}
              </p>
            ) : null}

            {/* THE QUANTITY PANEL: which bobbin, how much, what is left. */}
            {stage.kind === "qty" && qtyLine !== undefined ? (
              <section className="floor-panel" data-testid="floor-ship-qty-panel">
                <p className="floor-ship-unit-head" data-testid="floor-ship-qty-unit">
                  {t("sales.floor.ship.unitLine", {
                    serial: stage.unit.serialCode,
                    location: stage.unit.locationCode ?? "",
                    remaining: formatMeasure(stage.unit.remainingMeasure, { locale }),
                    uom: unitSymbol(stage.unit.measureUom ?? "", { locale }),
                  })}
                </p>
                <p className="floor-line-sub">
                  {qtyLine.line_no}. {qtyLine.description} —{" "}
                  {t("sales.floor.ship.lineOpen", {
                    open: formatMeasure(scaledToMeasureText(lineOpen), { locale }),
                    uom: unitSymbol(qtyLine.uom_code, { locale }),
                  })}
                </p>
                <div className="floor-field">
                  {/* A SPAN, not a `<label for>`: `QtyStepper` renders its
                      input with a testid and no `id`, so `htmlFor` here named
                      an element that does not exist — an association that
                      reads as present and resolves to nothing. The cut
                      screen's handle labels are the precedent
                      (`floor-cut-handle-label`), and floor-ship.css gives this
                      one the same weight `.floor-field label` would have. */}
                  <span className="floor-ship-qty-label">{t("sales.floor.ship.qty")}</span>
                  <QtyStepper
                    value={stage.qty}
                    onChange={(next) => {
                      setStage({ ...stage, qty: next });
                    }}
                    testId="floor-ship-qty"
                    precision={QTY_PRECISION}
                    locale={locale}
                  />
                </div>
                {canOfferFullUnit ? (
                  <FloorButton
                    variant="ghost"
                    onClick={() => {
                      const remaining = parseMeasure(stage.unit.remainingMeasure);
                      const whole =
                        remaining === undefined ? undefined : scaledToQtyText(remaining);
                      if (whole !== undefined) setStage({ ...stage, qty: whole });
                    }}
                    testId="floor-ship-full-unit"
                  >
                    {t("sales.floor.ship.fullUnit")}
                  </FloorButton>
                ) : null}
                {/* The live remainder, DISPLAY ONLY — exact in the shared frame,
                    silent when the frames differ (header). */}
                {remainderAfter !== undefined && remainderAfter >= 0n && !qtyInvalid ? (
                  <p
                    className="floor-ship-remaining"
                    data-testid="floor-ship-remaining"
                    // The canonical number beside the localized sentence: a
                    // walkthrough reads the remainder EXACTLY in every locale,
                    // whatever order a translation puts its two quantities in.
                    data-remaining={scaledToMeasureText(remainderAfter)}
                  >
                    {t("sales.floor.ship.remains", {
                      qty: formatMeasure(qtyText, { locale }),
                      uom: unitSymbol(qtyLine.uom_code, { locale, count: Number(qtyText) }),
                      remaining: formatMeasure(scaledToMeasureText(remainderAfter), { locale }),
                    })}
                  </p>
                ) : null}
              </section>
            ) : null}

            {/* THE LINE CARDS — every non-cancelled line of the order with what
                is still open and the units module 06 holds for it. A planned
                unit's row is a button: tapping it is the scan. */}
            <ul className="floor-lines" data-testid="floor-ship-lines">
              {plan.map((line) => {
                const open = openAfterQueued(line, queued);
                const pickable = isPickable(line, queued);
                const choosing = stage.kind === "line" && pickable;
                return (
                  <li
                    key={line.sales_order_line_id}
                    className={`floor-line${pickable ? "" : " floor-line-done"}${choosing ? " floor-ship-line-choosing" : ""}`}
                    data-testid="floor-ship-line"
                    data-line-id={line.sales_order_line_id}
                    data-open={scaledToWireQty(open)}
                    data-line-status={line.line_status}
                  >
                    <p className="floor-line-title">
                      {line.line_no}. {line.description}
                    </p>
                    <p className="floor-line-sub">
                      {pickable
                        ? t("sales.floor.ship.lineOpen", {
                            open: formatMeasure(scaledToMeasureText(open), { locale }),
                            uom: unitSymbol(line.uom_code, { locale }),
                          })
                        : lineStatusWord(t, line.line_status)}
                    </p>
                    {line.reserved_units.length > 0 ? (
                      <ul className="floor-ship-units">
                        {line.reserved_units.map((unit) => {
                          const words = t("sales.floor.ship.unitLine", {
                            serial: unit.serial_code,
                            location: unit.location_code,
                            remaining: formatMeasure(unit.remaining_measure, { locale }),
                            uom: unitSymbol(unit.measure_uom, { locale }),
                          });
                          return (
                            <li key={unit.stock_unit_id}>
                              {pickable && detail.status === "picking" && stage.kind === "scan" ? (
                                <button
                                  type="button"
                                  className="floor-ship-unit"
                                  data-testid="floor-ship-unit"
                                  data-unit-id={unit.stock_unit_id}
                                  disabled={busy}
                                  onClick={() => {
                                    enterQty(resolvedFromPlan(unit), line);
                                    floorFeedback("ok");
                                  }}
                                >
                                  {words}
                                </button>
                              ) : (
                                <p
                                  className="floor-ship-unit-inert"
                                  data-testid="floor-ship-unit"
                                  data-unit-id={unit.stock_unit_id}
                                >
                                  {words}
                                </p>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                    {choosing ? (
                      <FloorButton
                        variant="primary"
                        onClick={() => {
                          // `choosing` is `stage.kind === "line" && pickable`,
                          // so this branch only renders on the line stage and
                          // `stage.unit` is the scanned unit by construction.
                          enterQty(stage.unit, line);
                        }}
                        testId="floor-ship-line-choose"
                      >
                        {t("sales.floor.ship.confirm")}
                      </FloorButton>
                    ) : null}
                  </li>
                );
              })}
            </ul>

            {plan.length > 0 && !plan.some((line) => isPickable(line, queued)) && !closesOrder ? (
              <p className="floor-hint" data-testid="floor-ship-no-open-lines">
                {t("sales.floor.ship.noOpenLines")}
              </p>
            ) : null}

            {/* THE PICKED ROWS: what the server holds, then what this device
                holds. Undo is offered only while picking (the lines freeze at
                pack — 409 SHIPMENT_NOT_PICKING otherwise, so no button). */}
            {liveLines.length > 0 || queued.length > 0 ? (
              <ul className="floor-cards" data-testid="floor-ship-picks">
                {liveLines.map((row) => (
                  <li
                    key={row.id}
                    className="floor-card"
                    data-testid="floor-ship-picked"
                    data-line-id={row.sales_order_line_id}
                    data-unit-id={row.stock_unit_id}
                    data-is-cut={row.is_cut ? "true" : "false"}
                  >
                    <p className="floor-card-title">
                      {t("sales.floor.ship.pickedRow", {
                        qty: formatMeasure(row.qty_picked, { locale }),
                        uom: unitSymbol(row.uom_code, { locale, count: Number(row.qty_picked) }),
                        serial: row.serial_code,
                      })}
                    </p>
                    {detail.status === "picking" ? (
                      <FloorButton
                        variant="ghost"
                        disabled={busy}
                        onClick={() => void unpick(row)}
                        testId="floor-ship-unpick"
                      >
                        {t("sales.floor.ship.unpick")}
                      </FloorButton>
                    ) : null}
                  </li>
                ))}
                {queued.map((pick, index) => {
                  const line = plan.find(
                    (candidate) => candidate.sales_order_line_id === pick.lineId,
                  );
                  const unit = line?.reserved_units.find((u) => u.stock_unit_id === pick.unitId);
                  return (
                    <li
                      key={`${pick.unitId}:${pick.lineId}:${String(index)}`}
                      className="floor-card floor-ship-pending"
                      data-testid="floor-ship-picked"
                      data-line-id={pick.lineId}
                      data-unit-id={pick.unitId}
                      data-pending="true"
                    >
                      <p className="floor-card-title">
                        {t("sales.floor.ship.pickedRow", {
                          qty: formatMeasure(pick.qty, { locale }),
                          uom: unitSymbol(line?.uom_code ?? "", {
                            locale,
                            count: Number(pick.qty),
                          }),
                          serial: unit?.serial_code ?? shortId(pick.unitId),
                        })}
                      </p>
                      <p className="floor-badge floor-badge-warn">
                        {t("inventory.floor.scan.sessionWaiting")}
                      </p>
                    </li>
                  );
                })}
              </ul>
            ) : null}

            {/* The pre-press disclosure (D-088): BEFORE Pack and BEFORE Ship. */}
            {closesOrder ? (
              <p className="floor-ship-last" role="status" data-testid="floor-ship-last">
                {t("sales.floor.ship.last")}
              </p>
            ) : null}

            {/* A pack queued on this device: the note does not exist yet. */}
            {detail.status === "picking" ? noteBlock() : null}

            {detail.status === "packed" ? (
              <section className="floor-panel" data-testid="floor-ship-ship-panel">
                <p className="floor-line-sub">{t("sales.floor.ship.packedBody")}</p>
                <div className="floor-field">
                  <label htmlFor="floor-ship-carrier">{t("sales.floor.ship.carrier")}</label>
                  <input
                    id="floor-ship-carrier"
                    type="text"
                    autoComplete="off"
                    maxLength={120}
                    value={carrier}
                    onChange={(event) => {
                      setCarrier(event.target.value);
                    }}
                    data-testid="floor-ship-carrier"
                  />
                </div>
                <div className="floor-field">
                  <label htmlFor="floor-ship-tracking">{t("sales.floor.ship.tracking")}</label>
                  <input
                    id="floor-ship-tracking"
                    type="text"
                    autoComplete="off"
                    maxLength={120}
                    value={tracking}
                    onChange={(event) => {
                      setTracking(event.target.value);
                    }}
                    data-testid="floor-ship-tracking"
                  />
                </div>
                {/* The note drawn at pack (D-288), or why it cannot be opened
                    here and now. Its stated pending left with PR-D4. */}
                {noteBlock()}
              </section>
            ) : null}
          </>
        ) : null}
      </div>
    </FloorScreen>
  );
}
